import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardCheck,
  ShieldAlert,
  Fuel,
  Anchor,
  FileCheck2,
  Search,
  Shield,
  Radio,
  Compass,
  Beaker,
  Timer,
  ArrowUpRight,
} from 'lucide-react';
import { useI18n } from '@/i18n/I18nContext';
import { SERVICES } from '@/constants/testIds';
import { SERVICE_SLUGS } from '@/lib/services';

const iconMap = {
  'condition-prepurchase': ClipboardCheck,
  damage: ShieldAlert,
  'draft-bunker': Fuel,
  'on-off-hire': Anchor,
  'ism-isps-mlc': FileCheck2,
  'pre-vetting-sire-cdi': Search,
  'psc-inspection': Shield,
  'remote-vdr': Radio,
  'navigation-audit': Compass,
  'blending-services': Beaker,
  'loss-control-expediting': Timer,
};

export const ServicesGrid = () => {
  const { t } = useI18n();

  return (
    <section
      id="services"
      data-testid={SERVICES.gridRoot}
      className="relative py-24 sm:py-32 bg-[#040A12]"
    >
      <div className="container mx-auto px-6">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#C9A24E]" />
            <span className="text-[11px] uppercase tracking-[0.32em] text-[#C9A24E]">
              {t('services.eyebrow')}
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F2F5F8] leading-[1.1]">
            {t('services.title')}
          </h2>
          <p className="mt-6 text-[15px] leading-relaxed text-[#C9D2E2] max-w-2xl">
            {t('services.subtitle')}
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C9A24E]/15 border border-[#C9A24E]/15">
          {SERVICE_SLUGS.map((slug) => {
            const Icon = iconMap[slug] || ClipboardCheck;
            const name = t(`services.items.${slug}.name`);
            const short = t(`services.items.${slug}.short`);
            return (
              <Link
                key={slug}
                to={`/services#${slug}`}
                data-testid={SERVICES.card(slug)}
                className="group relative bg-[#0A1A2F] p-8 hover:bg-[#0E2240] transition-colors flex flex-col gap-6"
              >
                <div className="flex items-center justify-between">
                  <div className="h-12 w-12 flex items-center justify-center border border-[#C9A24E]/40 text-[#C9A24E] group-hover:border-[#C9A24E] transition-colors">
                    <Icon size={20} strokeWidth={1.4} />
                  </div>
                  <ArrowUpRight
                    size={18}
                    className="text-[#C9A24E]/50 group-hover:text-[#C9A24E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                  />
                </div>

                <div>
                  <h3 className="font-serif text-xl text-[#F2F5F8] leading-snug group-hover:text-[#C9A24E] transition-colors">
                    {name}
                  </h3>
                  <p className="mt-3 text-sm text-[#8F9BB3] leading-relaxed">{short}</p>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-7 py-4 border border-[#C9A24E] text-[#C9A24E] text-[12px] uppercase tracking-[0.22em] hover:bg-[#C9A24E]/10 transition-colors"
          >
            {t('services.viewDetails')}
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
